/**
 * @module components/ColorSetting
 * @description 词云颜色设置（ChartSettingsV2）
 *
 * 状态管理模式：
 *   读/写: useSettingObjectState('colorSetting')
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useIntl } from 'umi';
import { Bubble, Button, Select } from 'tea-component';
import ConfigCard from '@/pages/datalab/components/ConfigCard';
import ColorTheme from '../MapColorSelector/ColorTheme';
import CustomColorSelector from '@/pages/datalab/components/CustomColorSelector';
import {
  MULITI_COLORS_MAP,
  CUSTOM_HEXS,
  WORDCLOUD_COLORS_MAP,
} from '../MapColorSelector/constants';
import { useSettingObjectState } from '../../context';
import { useQuerySelector } from '../../hooks/deps';
import {
  cloneColorSettingDefault,
  type IColorSettingValue,
} from '../../types/settings/colorSetting';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';
import './index.scss';

const TYPE_LABEL_IDS: Record<string, string> = {
  pureColor: 'dbl_chart_color_setting_pure_color',
  multiColor: 'dbl_chart_color_setting_multi_color',
  sentimentRelated: 'dbl_chart_color_setting_sentiment_related',
  countRelated: 'dbl_chart_color_setting_count_related',
};

const ColorSetting: React.FC = () => {
  const { formatMessage } = useIntl();
  const { value, setValue } = useSettingObjectState('colorSetting');
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );

  const [type, setType] = useState<string>(value.type);

  const sentimentRelatedDisabled = !(
    columnMetrics[1] as Record<string, unknown>
  )?.column_display?.includes('sentiment');

  const handleChange = useCallback(
    (newValue: IColorSettingValue) => {
      setValue(newValue);
    },
    [setValue],
  );

  const onReset = useCallback(() => {
    handleChange(cloneColorSettingDefault());
  }, [handleChange]);

  // sentimentRelated 不可用时自动回退
  useEffect(() => {
    if (sentimentRelatedDisabled && value.type === 'sentimentRelated') {
      handleChange(cloneColorSettingDefault());
    }
  }, [sentimentRelatedDisabled, value.type, handleChange]);

  useEffect(() => {
    setType(value.type || 'pureColor');
  }, [value.type]);

  const options = useMemo(
    () => [
      {
        value: 'pureColor',
        changeValue: cloneColorSettingDefault(),
        text: (
          <Bubble
            content={formatMessage({
              id: 'dbl_chart_color_setting_pure_color_tip',
            })}
            placement="left"
            placementOffset={[0, 18]}
            overlayClassName="dbl-chart-color-setting-bubble"
          >
            <div>
              {formatMessage({ id: 'dbl_chart_color_setting_pure_color' })}
            </div>
          </Bubble>
        ),
        component: value.type === 'pureColor' && (
          <CustomColorSelector
            colorInfo={
              value.colors?.[0] || { hexa: '#5086F3', alpha: '100' }
            }
            onChangeColorInfo={(colorInfo: { hexa: string; alpha: string }) => {
              handleChange({
                type: 'pureColor',
                colors: [
                  { hexa: colorInfo.hexa, alpha: colorInfo.alpha },
                ],
              });
            }}
            presetColors={CUSTOM_HEXS}
          />
        ),
      },
      {
        value: 'multiColor',
        changeValue: {
          type: 'multiColor',
          colorName: 'light',
          colors: MULITI_COLORS_MAP?.light?.colors?.map((c: string) => ({
            hexa: c,
            alpha: '100',
          })),
        },
        text: (
          <Bubble
            content={formatMessage({
              id: 'dbl_chart_color_setting_multi_color_tip',
            })}
            placement="left"
            placementOffset={[0, 18]}
            overlayClassName="dbl-chart-color-setting-bubble"
          >
            <div>
              {formatMessage({ id: 'dbl_chart_color_setting_multi_color' })}
            </div>
          </Bubble>
        ),
        component: value.type === 'multiColor' && (
          <ColorTheme
            {...value}
            colorMap={MULITI_COLORS_MAP}
            label="dbl_chart_map_color"
            onChange={(v: IColorSettingValue) =>
              handleChange({ type: 'multiColor', ...v })
            }
          />
        ),
      },
      {
        value: 'sentimentRelated',
        disabled: sentimentRelatedDisabled,
        changeValue: {
          type: 'sentimentRelated',
          colors: [
            { hexa: '#29B569', alpha: '100' },
            { hexa: '#6E7794', alpha: '100' },
            { hexa: '#EF5858', alpha: '100' },
          ],
        },
        text: (
          <Bubble
            content={formatMessage({
              id: 'dbl_chart_color_setting_sentiment_related_tip',
            })}
            placement="left"
            placementOffset={[0, 18]}
            overlayClassName="dbl-chart-color-setting-bubble"
          >
            <div>
              {formatMessage({
                id: 'dbl_chart_color_setting_sentiment_related',
              })}
            </div>
          </Bubble>
        ),
        component: value.type === 'sentimentRelated' && (
          <div className="dbl-chart-color-setting-sentiment">
            {[
              {
                text: formatMessage({
                  id: 'dbl_chart_color_setting_sentiment_related_positive',
                }),
                color: '#29B569',
              },
              {
                text: formatMessage({
                  id: 'dbl_chart_color_setting_sentiment_related_neutral',
                }),
                color: '#6E7794',
              },
              {
                text: formatMessage({
                  id: 'dbl_chart_color_setting_sentiment_related_negative',
                }),
                color: '#EF5858',
              },
            ].map((item, index) => (
              <div key={index} className="dbl-chart-color-setting-sentiment-item">
                <div
                  className="dbl-chart-color-setting-sentiment-item-icon"
                  style={{ background: item.color }}
                />
                <div className="dbl-chart-color-setting-sentiment-item-text">
                  {item.text}
                </div>
              </div>
            ))}
          </div>
        ),
      },
      {
        value: 'countRelated',
        changeValue: {
          type: 'countRelated',
          colorName: 'blue',
          colors: WORDCLOUD_COLORS_MAP?.blue?.colors?.map((c: string) => ({
            hexa: c,
            alpha: '100',
          })),
        },
        text: (
          <Bubble
            content={formatMessage({
              id: 'dbl_chart_color_setting_count_related_tip',
            })}
            placement="left"
            placementOffset={[0, 18]}
            overlayClassName="dbl-chart-color-setting-bubble"
          >
            <div>
              {formatMessage({ id: 'dbl_chart_color_setting_count_related' })}
            </div>
          </Bubble>
        ),
        component: value.type === 'countRelated' && (
          <ColorTheme
            {...value}
            colorMap={WORDCLOUD_COLORS_MAP}
            onChange={(v: IColorSettingValue) =>
              handleChange({ type: 'countRelated', ...v })
            }
          />
        ),
      },
    ],
    [formatMessage, value, handleChange, sentimentRelatedDisabled],
  );

  const activeOption = options.find(o => o.value === type);
  const themeButtonText = formatMessage({
    id: TYPE_LABEL_IDS[type] || TYPE_LABEL_IDS.pureColor,
  });

  return (
    <ConfigCard
      className="dbl-chart-color-setting"
      title={formatMessage({ id: 'dbl_chart_color_setting_title' })}
      operation={
        <Button
          type="link"
          onClick={event => {
            event?.preventDefault();
            onReset();
          }}
        >
          {formatMessage({ id: 'dbl_chart_color_setting_restore' })}
        </Button>
      }
    >
      <div className="dbl-chart-color-setting-theme">
        <span className="dbl-chart-color-setting-theme-label">
          {formatMessage({ id: 'dbl_chart_color_setting_color_theme' })}
        </span>
        <Select
          className="dbl-chart-color-setting-theme-select"
          appearance="button"
          matchButtonWidth
          options={options}
          value={type}
          button={themeButtonText}
          listWidth={210}
          listHeight={200}
          onChange={(selectedValue: string) => {
            setType(selectedValue);
            const selectedOpt = options.find(o => o.value === selectedValue);
            handleChange(
              selectedOpt?.changeValue || {
                type: selectedValue,
                colors: [],
              },
            );
          }}
        />
      </div>
      {activeOption?.component &&
        (type === 'pureColor' || type === 'sentimentRelated' ? (
          <div className="dbl-chart-color-setting-picker">
            {activeOption.component}
          </div>
        ) : (
          activeOption.component
        ))}
    </ConfigCard>
  );
};

export default React.memo(ColorSetting);
